import { t, TFunction } from "i18next";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Icons from "@app/assets/Icons";
import Loader from "@app/components/Loader";
import Pagination from "@app/components/Pagination";
import TableHeader from "@app/components/TableHeader";
import ViewUtilityHeader from "@app/components/ViewUtilityHeader";
import { RouteNames } from "@app/routes/index";
import { getSalesChannels } from "@app/services/Endpoints/SalesChannel.API";
import { HeadersMapInterface } from "@app/types/Common.types";
import { SalesChannels } from "@app/types/Service.type";
import { PLACEHOLDERS } from "@app/utils/constant";
import { AppContext } from "@app/utils/contexts/App.context";
import { FilterContext } from "@app/utils/contexts/Filters.context";

import "./index.scss";

let totalPages = 0;
const sortByEnum = {
    DESC: 1,
    ASC: 0,
};


const getHeadersMap = (t: TFunction): HeadersMapInterface => ({
    serialNo: {
        label: t("VIEW.SALESCHANNELS.SERIALNO"),
        layout: { lg: 2, sm: 2 },
        disableSort: true,
    },
    posName: {
        label: t("VIEW.SALESCHANNELS.POSNAME"),
        layout: { lg: 3, sm: 3 },
    },
    name: {
        label: t("VIEW.SALESCHANNELS.NAME"),
        layout: { lg: 2, sm: 2 },
    },
    status: {
        label: t("VIEW.SALESCHANNELS.STATUS"),
        layout: { lg: 2, sm: 2 },
    },
});

const ViewSalesChannelPage: FC = () => {
    const navigate = useNavigate();
    const { userPermissions } = useContext(AppContext);
    const { statusesFilter } = useContext(FilterContext);
    const HeadersMap = useMemo(() => getHeadersMap(t), [t]);

    const [salesChannels, setSalesChannels] = useState<SalesChannels[]>([]);
    const [sortByObj, setSortByObj] = useState({ sortBy: "posName", sortOrder: sortByEnum.ASC });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [loader, setLoader] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>("");
    const [isScrollComing, setIsScrollComing] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setPageNumber(1);
    }, [statusesFilter]);

    useEffect(() => {
        const filters = [statusesFilter?.map((status) => `&Status=${status}`).join("")].join("&");

        getSalesChannelsHandler(
            sortByObj.sortBy,
            sortByObj.sortOrder,
            pageNumber,
            pageSize,
            searchVal,
            filters,
        );
    }, [pageNumber, pageSize, sortByObj, searchVal, statusesFilter]);

    useEffect(() => {
        if (ref.current?.offsetWidth !== ref.current?.clientWidth) {
            setIsScrollComing(true);
        } else {
            setIsScrollComing(false);
        }
    });

    const getSalesChannelsHandler = (
        sortBy: string,
        sortOrder: number,
        pageNumber: number,
        pageSize: number,
        name?: string,
        filters?: string,
    ) => {
        setLoader(true);
        getSalesChannels(sortBy, sortOrder, pageNumber, pageSize, name, filters)
            .then((res) => {
                totalPages = res.data.totalPages;
                const result = res?.data?.data;
                if (result) {
                    setSalesChannels([...result]);
                } else {
                    setSalesChannels([]);
                }
            })
            .catch((err) => console.error(err))
            .finally(() => setLoader(false));
    };

    const sortByHandler = (headerKey: string) => {
        setSortByObj({ sortBy: headerKey, sortOrder: Number(!sortByObj.sortOrder) });
    };

    const onChangeRecordsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        setPageSize(Number(value));
        setPageNumber(1);
    };

    const onPageChange = (pageNumber: number) => {
        setPageNumber(Number(pageNumber));
    };

    const handleSearch = (val: string) => {
        setSearchVal(val);
    };

    const handleAddPlaceholder = () => {
        navigate(RouteNames.Add);
    };

    const handleEditPlaceholder = (item: SalesChannels) => {
        navigate(RouteNames.Edit, {
            state: { salesChannelEditData: item },
        });
    };

    return (
        <Container fluid className='salesChannel'>
            <ViewUtilityHeader
                onChange={onChangeRecordsPerPage}
                placeholder={PLACEHOLDERS.SALES_CHANNEL_SEARCH}
                btnLabel={t("VIEW.SALESCHANNELS.ADDSALESCHANNEL")}
                onClick={userPermissions?.["Create_Sales Channel"] ? handleAddPlaceholder : undefined}
                onSearch={handleSearch}
            />

            <TableHeader
                headersMap={HeadersMap}
                sortByHandler={sortByHandler}
                sortByObj={sortByObj}
                enableIconColumn
            />

            <div
                className={`salesChannel--page ${isScrollComing ? "salesChannel--isScroll" : ""}`}
                id='listing-Outercontainer'
                ref={ref}
            >
                {loader ? (
                    <Loader />
                ) : salesChannels.length ? (
                    salesChannels.map((item, index) => (
                        <Row
                            key={item.id || index}
                            className={`salesChannel__rowWrap ${index % 2 === 0 ? "salesChannel__rowGrey" : ""}`}
                        >
                            <Col className='salesChannel--pl20' sm='2' lg='2'>
                                {pageNumber === 1 && index < 9 ? "0" : ""}
                                {(pageNumber - 1) * pageSize + (index + 1)}
                            </Col>
                            <Col sm='3' lg='3'>
                                {item.posName}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.name}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.status ? t("VIEW.SALESCHANNELS.ACTIVE") : t("VIEW.SALESCHANNELS.INACTIVE")}
                            </Col>
                            <Col sm='1' lg='1'>
                                {
                                    userPermissions?.["Edit_Sales Channel"] && (
                                        <img
                                            className='salesChannel__enablepointer salesChannel--editIcon'
                                            src={Icons.EditIcon}
                                            alt={t("COMMON.EDIT") as string}
                                            onClick={() => handleEditPlaceholder(item)}
                                        />
                                    )
                                }
                            </Col >
                        </Row >
                    ))
                ) : (
                    <div className='salesChannel--noData'>{t("APIMSG.NODATA")}</div>
                )}
            </div >

            <Pagination
                sortByObj={sortByObj}
                onPageChange={onPageChange}
                totalPages={totalPages}
                pageSize={pageSize}
                pageNumber={pageNumber}
            />
        </Container >
    );
};

export default ViewSalesChannelPage;
